import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Users as UsersIcon, ShieldCheck, UserCog, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/constants";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const changeRole = async (uid, role) => {
    try { await api.put(`/users/${uid}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const changeStatus = async (uid, status) => {
    try { await api.put(`/users/${uid}/status`, { status }); toast.success(status === "disabled" ? "Access disabled" : "Access enabled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <header className="bg-navy px-6 md:px-8 py-6">
        <p className="overline text-gold mb-1.5">Admin Only</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight leading-none flex items-center gap-2">
          <UsersIcon className="h-6 w-6 text-gold" aria-hidden="true" /> User Access
        </h1>
        <p className="text-slate-300 text-sm mt-2">Any @complyxgov.com account may sign in as a Sales User. Promote approved users to Admin. Disable access to revoke.</p>
      </header>

      <div className="p-6 md:p-8">
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-border text-left">
                  {["Email", "Name", "Role", "Status", "Last login", "Date created", "Actions"].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold text-navy text-xs uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.user_id === user.user_id;
                  const disabled = u.status === "disabled";
                  return (
                    <tr key={u.user_id} className="border-b border-border last:border-0" data-testid={`user-row-${u.user_id}`}>
                      <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                      <td className="px-4 py-3 font-medium text-navy">{u.name || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] px-2 py-0.5 rounded ${u.role === "admin" ? "bg-gold/20 text-gold-dark" : "bg-slate-100 text-slate-600"}`}>
                          {u.role === "admin" ? "Admin" : "Sales User"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] px-2 py-0.5 rounded ${disabled ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-800"}`}>
                          {disabled ? "Disabled" : "Active"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{u.last_login ? fmtDate(u.last_login) : "—"}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3">
                        {isSelf ? (
                          <span className="text-xs text-muted-foreground">You</span>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {u.role === "admin" ? (
                              <Button size="sm" variant="outline" data-testid={`demote-${u.user_id}`} onClick={() => changeRole(u.user_id, "sales")}>
                                <UserCog className="h-3.5 w-3.5 mr-1" /> Demote
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" data-testid={`promote-${u.user_id}`} onClick={() => changeRole(u.user_id, "admin")}>
                                <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Promote to Admin
                              </Button>
                            )}
                            {disabled ? (
                              <Button size="sm" variant="outline" data-testid={`enable-${u.user_id}`} onClick={() => changeStatus(u.user_id, "active")} className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Enable
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" data-testid={`disable-${u.user_id}`} onClick={() => changeStatus(u.user_id, "disabled")} className="text-red-600 border-red-200 hover:bg-red-50">
                                <Ban className="h-3.5 w-3.5 mr-1" /> Disable
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="rounded-md border border-border bg-card p-5">
            <h3 className="font-display font-bold text-navy mb-2">Admin</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">Views scoring logic, numeric scores, API keys, enrichment logs, and admin settings. Can manage users.</p>
          </div>
          <div className="rounded-md border border-border bg-card p-5">
            <h3 className="font-display font-bold text-navy mb-2">Sales User</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">Views leads, buckets, next actions, offers, sales assistant drafts, notes, and deal stages. Cannot see API keys, full scoring logic, or admin settings.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
